import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import NotificationSnackbar from '../../components/ui/NotificationSnackbar';
import { useExperienceStorefront } from '../../components/website/StorefrontExperience';
import {
  fetchStorefrontIntegrations,
  fetchStorefrontMenuPages,
  saveStorefrontIntegrations,
} from '../../services/storefrontAdminService';

const emptyIntegration = (id = '') => ({ enabled: Boolean(String(id || '').trim()), id, scope: 'all', page_slugs: [] });

const normalizeTracking = (tracking = {}) => ({
  gtm: typeof tracking.gtm === 'object' ? tracking.gtm : emptyIntegration(tracking.gtm_id || ''),
  ga4: typeof tracking.ga4 === 'object' ? tracking.ga4 : emptyIntegration(tracking.ga4_id || ''),
  meta_pixel: typeof tracking.meta_pixel === 'object' ? tracking.meta_pixel : emptyIntegration(tracking.meta_pixel_id || ''),
  hotjar: typeof tracking.hotjar === 'object' ? tracking.hotjar : emptyIntegration(tracking.hotjar_site_id || ''),
  clarity: typeof tracking.clarity === 'object' ? tracking.clarity : emptyIntegration(tracking.clarity_project_id || ''),
});

const integrationDefinitions = [
  { key: 'gtm', title: 'Google Tag Manager', label: 'Container ID', placeholder: 'GTM-XXXXXXX' },
  { key: 'ga4', title: 'Google Analytics 4', label: 'Measurement ID', placeholder: 'G-XXXXXXXXXX' },
  { key: 'meta_pixel', title: 'Meta Pixel', label: 'Pixel ID', placeholder: '1234567890' },
  { key: 'hotjar', title: 'Hotjar', label: 'Site ID', placeholder: '1234567' },
  { key: 'clarity', title: 'Microsoft Clarity', label: 'Project ID', placeholder: 'abc123xyz' },
];

function IntegrationCard({ definition, value, pages, onChange }) {
  const selectedPages = useMemo(
    () => pages.filter((page) => (value.page_slugs || []).includes(page.slug)),
    [pages, value.page_slugs]
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={800}>{definition.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure the public identifier and where this storefront should load it.
            </Typography>
          </Box>
          <Switch
            checked={!!value.enabled}
            onChange={(event) => onChange({ ...value, enabled: event.target.checked })}
          />
        </Stack>

        <TextField
          fullWidth
          margin="normal"
          label={definition.label}
          placeholder={definition.placeholder}
          value={value.id || ''}
          disabled={!value.enabled}
          onChange={(event) => onChange({ ...value, id: event.target.value })}
        />

        <TextField
          select
          fullWidth
          margin="dense"
          label="Load on"
          value={value.scope || 'all'}
          disabled={!value.enabled}
          onChange={(event) => onChange({ ...value, scope: event.target.value, page_slugs: event.target.value === 'all' ? [] : value.page_slugs || [] })}
        >
          <MenuItem value="all">All storefront pages</MenuItem>
          <MenuItem value="selected_pages">Selected CMS pages</MenuItem>
        </TextField>

        {value.scope === 'selected_pages' && (
          <Autocomplete
            multiple
            options={pages}
            value={selectedPages}
            getOptionLabel={(option) => `${option.title || option.slug} · /${option.slug}`}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            onChange={(_event, selected) => onChange({ ...value, page_slugs: selected.map((page) => page.slug) })}
            renderInput={(params) => (
              <TextField {...params} margin="dense" label="CMS pages" placeholder="Choose pages" />
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function StorefrontIntegrations() {
  const storefront = useExperienceStorefront();
  const [form, setForm] = useState({ tracking: normalizeTracking(), cookie_consent: {} });
  const [pages, setPages] = useState([]);
  const [note, setNote] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (!storefront?.code) return;
    Promise.all([
      fetchStorefrontIntegrations(storefront.code),
      fetchStorefrontMenuPages(storefront.code),
    ])
      .then(([integrations, pageResult]) => {
        setForm({
          tracking: normalizeTracking(integrations.integrations?.tracking || {}),
          cookie_consent: integrations.integrations?.cookie_consent || {},
        });
        setPages(pageResult.pages || []);
      })
      .catch(() => setNote({ open: true, message: 'Failed to load integrations', severity: 'error' }));
  }, [storefront?.code]);

  const patchTracking = (key, value) => {
    setForm((current) => ({ ...current, tracking: { ...(current.tracking || {}), [key]: value } }));
  };

  const patchConsent = (key, value) => {
    setForm((current) => ({ ...current, cookie_consent: { ...(current.cookie_consent || {}), [key]: value } }));
  };

  const save = async () => {
    try {
      await saveStorefrontIntegrations(storefront.code, form);
      setNote({ open: true, message: 'Integrations saved', severity: 'success' });
    } catch (error) {
      setNote({ open: true, message: error.response?.data?.details || 'Save failed', severity: 'error' });
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Integrations</Typography>
          <Typography color="text.secondary">
            Storefront tracking and analytics only. Credentials and payment gateway secrets remain centrally managed.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}>Save</Button>
      </Stack>

      <Grid container spacing={2}>
        {integrationDefinitions.map((definition) => (
          <Grid item xs={12} lg={6} key={definition.key}>
            <IntegrationCard
              definition={definition}
              value={form.tracking?.[definition.key] || emptyIntegration()}
              pages={pages}
              onChange={(value) => patchTracking(definition.key, value)}
            />
          </Grid>
        ))}

        <Grid item xs={12} lg={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800}>Cookie consent</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Controls the storefront consent banner. It does not store third-party credentials.
              </Typography>
              <FormControlLabel
                control={<Switch checked={!!form.cookie_consent?.enabled} onChange={(event) => patchConsent('enabled', event.target.checked)} />}
                label="Enabled"
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Consent message"
                value={form.cookie_consent?.message || ''}
                onChange={(event) => patchConsent('message', event.target.value)}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <NotificationSnackbar
        open={note.open}
        message={note.message}
        severity={note.severity}
        onClose={() => setNote((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
}
